/**
 * Dealer Package Acceptance Page
 * Mobile-optimized interface for dealers to accept packages using OTP verification
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { packageAssignmentService, PackageAssignment } from '../services/package-assignment.service';
import { otpService } from '../services/otp.service';
import { Button, Input, Alert } from '../components/ui';

export default function DealerPackageAcceptancePage() {
  const { qrToken } = useParams<{ qrToken: string }>();
  const navigate = useNavigate();
  
  const [assignment, setAssignment] = useState<PackageAssignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // OTP state
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number>(3);

  useEffect(() => {
    if (qrToken) {
      loadPackageAssignment();
    }
  }, [qrToken]);

  // Countdown timer for OTP expiry
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setShowOTPInput(false);

            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeRemaining]);

  const loadPackageAssignment = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!qrToken) {
        setError('Invalid QR code');
        return;
      }

      const result = await packageAssignmentService.getAssignmentByQRToken(qrToken);
      
      if (result.success && result.assignment) {
        setAssignment(result.assignment);
        
        // Check if already accepted
        if (result.assignment.status === 'accepted') {
          setSuccess('This package has already been accepted!');
        } else if (result.assignment.status === 'expired') {
          setError('This package assignment has expired. Please contact the manufacturer.');
        }
      } else {
        setError(result.error || 'Package not found or invalid QR code');
      }
    } catch (err: any) {
      console.error('Failed to load package assignment:', err);
      setError('Failed to load package information');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptPackage = async () => {
    if (!assignment) return;

    try {
      setOtpLoading(true);
      setError(null);
      
      const result = await otpService.generateOTP(assignment.dealer.id, assignment.id);
      
      if (result.success) {
        setShowOTPInput(true);
        setAttemptsRemaining(result.attemptsRemaining || 3);
        
        // Set countdown timer (5 minutes = 300 seconds)
        if (result.expiryTime) {
          const expiryTime = new Date(result.expiryTime).getTime();
          const now = Date.now();
          const timeLeft = Math.max(0, Math.floor((expiryTime - now) / 1000));
          setTimeRemaining(timeLeft);
        } else {
          setTimeRemaining(300); // Default 5 minutes
        }
        
        setSuccess('OTP sent to your registered mobile number!');
      } else {
        setError(result.error || 'Failed to send OTP');
      }
    } catch (err: any) {
      console.error('Failed to generate OTP:', err);
      setError('Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!assignment || !otp) return;

    try {
      setOtpLoading(true);
      setError(null);
      
      const result = await otpService.verifyOTP(assignment.dealer.id, assignment.id, otp);
      
      if (result.success && result.packageActivated) {
        setSuccess('Package accepted successfully! The package is now activated and assigned to you.');
        setShowOTPInput(false);
        setOtp('');
        
        // Update assignment status
        setAssignment(prev => prev ? { ...prev, status: 'accepted' as const } : null);
      } else {
        setError(result.error || 'Invalid OTP');
        setAttemptsRemaining(result.attemptsRemaining || 0);
        
        if (result.attemptsRemaining === 0) {
          setShowOTPInput(false);
          setError('Maximum attempts exceeded. Please request a new OTP.');
        }
      }
    } catch (err: any) {
      console.error('Failed to verify OTP:', err);
      setError('Failed to verify OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!assignment) return;

    try {
      setOtpLoading(true);
      setError(null);
      
      const result = await otpService.resendOTP(assignment.dealer.id, assignment.id);
      
      if (result.success) {
        setAttemptsRemaining(result.attemptsRemaining || 3);
        
        // Reset countdown timer
        if (result.expiryTime) {
          const expiryTime = new Date(result.expiryTime).getTime();
          const now = Date.now();
          const timeLeft = Math.max(0, Math.floor((expiryTime - now) / 1000));
          setTimeRemaining(timeLeft);
        } else {
          setTimeRemaining(300);
        }
        
        setSuccess('OTP resent to your mobile number!');
      } else {
        setError(result.error || 'Failed to resend OTP');
      }
    } catch (err: any) {
      console.error('Failed to resend OTP:', err);
      setError('Failed to resend OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading package information...</p>
        </div>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Package Not Found</h2>
          <p className="text-gray-600 mb-4">
            The QR code you scanned is invalid or the package assignment could not be found.
          </p>
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}
          <Button onClick={() => navigate('/')} variant="outline">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg mb-4 p-6">
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Package Assignment</h1>
            <p className="text-sm text-gray-600">From {assignment.manufacturer?.name}</p>
          </div>

          {/* Status Badge */}
          <div className="text-center mb-4">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(assignment.status)}`}>
              {assignment.status.charAt(0).toUpperCase() + assignment.status.slice(1)}
            </span>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-4">
            {success}
          </Alert>
        )}

        {/* Package Details */}
        <div className="bg-white rounded-lg shadow-lg mb-4 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Package Details</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Package ID:</span>
              <span className="text-sm font-medium text-gray-900">{assignment.packageId}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Type:</span>
              <span className="text-sm font-medium text-gray-900 capitalize">{assignment.packageType}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Quantity:</span>
              <span className="text-sm font-medium text-gray-900">{assignment.packageDetails.quantity}</span>
            </div>
            
            <div className="border-t pt-3">
              <span className="text-sm text-gray-600">Contents:</span>
              <p className="text-sm font-medium text-gray-900 mt-1">{assignment.packageDetails.contents}</p>
            </div>
            
            {assignment.packageDetails.description && (
              <div className="border-t pt-3">
                <span className="text-sm text-gray-600">Description:</span>
                <p className="text-sm text-gray-900 mt-1">{assignment.packageDetails.description}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dealer Info */}
        <div className="bg-white rounded-lg shadow-lg mb-4 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Assigned To</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Dealer:</span>
              <span className="text-sm font-medium text-gray-900">{assignment.dealer.name}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Mobile:</span>
              <span className="text-sm font-medium text-gray-900">{assignment.dealer.mobileNumber}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Assigned:</span>
              <span className="text-sm text-gray-900">
                {new Date(assignment.assignedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Expires:</span>
              <span className="text-sm text-gray-900">
                {new Date(assignment.expiresAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Action Section */}
        {assignment.status === 'accepted' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <h3 className="text-lg font-semibold text-green-900 mb-2">Package Accepted!</h3>
            <p className="text-sm text-green-700">
              This package has been successfully accepted and activated.
            </p>
          </div>
        ) : assignment.status === 'expired' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-red-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-lg font-semibold text-red-900 mb-2">Assignment Expired</h3>
            <p className="text-sm text-red-700 mb-4">
              This package assignment has expired. Please contact the manufacturer for a new assignment.
            </p>
          </div>
        ) : !showOTPInput ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">Accept Package</h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Click the button below to receive an OTP on your registered mobile number to accept this package.
            </p>
            
            <Button
              onClick={handleAcceptPackage}
              disabled={otpLoading}
              fullWidth
              className="mb-4"
            >
              {otpLoading ? 'Sending OTP...' : 'Accept Package'}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              You will receive a 6-digit OTP on {assignment.dealer.mobileNumber}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">Enter OTP</h2>
            
            {timeRemaining > 0 && (
              <div className="text-center mb-4">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {formatTime(timeRemaining)}
                </div>
                <p className="text-xs text-gray-500">Time remaining</p>
              </div>
            )}
            
            <div className="mb-4">
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>
            
            <div className="flex gap-3 mb-4">
              <Button
                onClick={handleVerifyOTP}
                disabled={otpLoading || otp.length !== 6}
                fullWidth
              >
                {otpLoading ? 'Verifying...' : 'Verify OTP'}
              </Button>
            </div>
            
            <div className="text-center">
              <button
                onClick={handleResendOTP}
                disabled={otpLoading || timeRemaining > 240} // Allow resend in last minute
                className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
              >
                Resend OTP
              </button>
            </div>
            
            {attemptsRemaining < 3 && (
              <p className="text-xs text-orange-600 text-center mt-2">
                {attemptsRemaining} attempts remaining
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}